import {
  prop,
  propEq,
  find,
  compose,
  reduce,
  filter,
  equals,
  map,
  sortBy,
  isNil
} from 'ramda'
import { langStorage } from '../../i18n/i18nUtils'
import { useStore } from '../../StateProvider'

/**
 * @param {number} tier
 */
const filterByTier = (tier) => filter(field => field.tier === tier)

/**
 * @param {string} name
 */
const filterFields = (name) => filter(({ type }) => equals(type, name))

const filterFlagFields = filterFields('flag')

/**
 * @param {{string: string | number}} formStore
 */
const additionalFldValues = (formStore) =>
  compose(
    reduce((acc, fld) => {
      const value = formStore[fld.name]
      // for fields in WL but hidden & idScan that's sent separately
      if (isNil(value) || fld.name === 'idScan' || fld.name === 'idBackSideScan') return { ...acc }
      return {
        ...acc,
        [fld.name]: value
      }
    }, {}),
    filter(fld => fld.type !== 'docs')
  )
/**
 * @param {string} key
 * @param {{string: string}[]} texts
 * @param {string} defaultTxt
 */
const getTextByLang = (key, texts = [], defaultTxt = '') => {
  const lang = langStorage.getShort()
  /**
   * @param {string} value
   */
  const getObjByName = (value) => find(propEq('name', value))
  const translation = getObjByName(`${key}.${lang}`)(texts)
  const translationEng = getObjByName(`${key}.en`)(texts)
  return prop('text', translation) || prop('text', translationEng) || defaultTxt
}

const createStoreValidation = (extFlds, mobileVerificationEnabled) => {
  const storeValidationByTier2 = createStoreValidationByTier(extFlds, 2, mobileVerificationEnabled)
  return {
    tier1: { 0: createStoreValidationByTier(extFlds, 1, mobileVerificationEnabled) },
    tier2: {
      0: { ...storeValidationByTier2 },
      1: { ...storeValidationByTier2 }
    },
    tier3: { 0: createStoreValidationByTier(extFlds, 3, mobileVerificationEnabled) }
  }
}

const createStoreValidationByTier = (extFlds, tier, mobileVerificationEnabled) => {
  const removeFromValidationFields = ['employmentDetails', 'lastEmploymentDate']
  const filteredExtFlds = compose(
    filter(fld => !removeFromValidationFields.includes(fld.name)),
    filter(fld => fld.required && fld.tier === tier)
  )(extFlds)

  let validation = filteredExtFlds.reduce((acc, fld) => ({ ...acc, [fld.name]: false }), {})
  const isPhoneOnCurrentTier = filteredExtFlds.find(item => item.name === 'phoneNumber')
  if (isPhoneOnCurrentTier && mobileVerificationEnabled) {
    validation = {
      ...validation,
      mobileVerification: false
    }
  }
  return validation
}
/**
 * @param {OptionsInput} props
 * @returns {OptionProps}
 */
const getOption = ({ value: label, id, ...rest }) => ({
  value: id,
  label,
  ...rest
})

const sortByOrder = sortBy(prop('sortOrder'))

const getOptions = compose(
  map(getOption),
  sortByOrder
)

const getUserValue = (accId, user, name) => {
  return accId === 0 ? user[name] : user.jointAccounts?.[accId - 1]?.[name]
}

const radioValueConverter = (value) => {
  const converter = {
    true: 'Y',
    false: 'N'
  }
  if (typeof value === 'boolean') {
    return converter[value]
  }
  return value
}

/**
 *
 * @param {string} recIndex
 */
const isEmptyNewDocLoader = (recIndex) => /^-?\d+$/.test(recIndex)

/**
 @param {string} name
 */
const useGetUserOrFormValue = (name) => {
  const [{
    user,
    form,
    form: { accId }
  }] = useStore()
  const formValue = form[accId]?.[name]
  const userValue = getUserValue(accId, user, name)
  return userValue == null ? formValue : userValue
}

const useGetPlatform = () => {
  const [{ whitelabel }] = useStore()
  const userOrFormValue = useGetUserOrFormValue('platform')
  return userOrFormValue == null ? whitelabel?.targetPlatform : userOrFormValue
}

export {
  filterByTier,
  filterFlagFields,
  additionalFldValues,
  getTextByLang,
  createStoreValidation,
  getOptions,
  filterFields,
  getUserValue,
  radioValueConverter,
  createStoreValidationByTier,
  isEmptyNewDocLoader,
  useGetUserOrFormValue,
  useGetPlatform
}
